// Client-side navigation guard for signed-out and wrong-role visitors. This is
// a UX boundary only; the BFF authorizes every request independently.
import Cookies from "js-cookie";
import { redirect } from "@tanstack/react-router";
import {
  getUserIdFromToken,
  getUserRoleFromToken,
} from "@/helpers/utils/routing-token-claims";

const PROTECTED_ROUTES: Record<string, readonly string[]> = {
  "/admin": ["ohs_admin", "sifa", "ama", "external_sifa"],
  "/employee": ["employee"],
  "/dashboard": [
    "ohs_officer",
    "location_manager",
    "sifa",
    "ama",
    "ohs_admin",
    "external_sifa",
  ],
  "/clients": [
    "ohs_officer",
    "location_manager",
    "sifa",
    "ama",
    "ohs_admin",
    "external_sifa",
  ],
  "/inspections": [
    "ohs_admin",
    "location_manager",
    "sifa",
    "ama",
    "external_sifa",
  ],
  "/auth/register/invite": [
    "ohs_admin",
    "sifa",
    "external_sifa",
    "ohs_officer",
  ],
  "/auth/register/add-employees": ["ohs_officer"],
};

const PUBLIC_ROUTES = [
  "/auth/login",
  "/auth/register",
  "/auth/register/code",
  "/auth/register/code/success",
  "/auth/register/dearest",
  "/auth/forgot-password",
  "/auth/forgot-password/set",
  "/auth/forgot-password/success",
  "/auth/sso-callback",
] as const;

// Routes that open for everyone, whatever session the visitor holds, and
// never redirect: a mailed work-email confirmation link carries its own
// proof, and the person following it may be signed in to the portal as
// somebody, as nobody, or be mid-refresh.
const OPEN_ROUTES = ["/auth/confirm-work-email"] as const;

// Routes that redirect an already-authenticated visitor onwards. A callback
// may never point back at one of these: honoring it would redirect straight
// back into this same branch, recreating the identical request forever.
const AUTH_REDIRECT_ROUTES: readonly string[] = [
  ...PUBLIC_ROUTES,
  "/auth/refresh",
];

// A same-origin, in-app path: single leading slash (rules out
// protocol-relative `//host` and absolute `scheme://host` open-redirect
// targets a query param could otherwise carry).
function isSameOriginPath(path: string | null): path is string {
  return !!path && path.startsWith("/") && !path.startsWith("//");
}

function isAuthRedirectRoute(callback: string): boolean {
  return AUTH_REDIRECT_ROUTES.includes(callback.split(/[?#]/)[0]!);
}

const ROLE_REDIRECTS: Record<string, (userId: string) => string> = {
  ohs_admin: (id) => `/admin/${id}`,
  ohs_officer: () => "/dashboard",
  location_manager: () => "/dashboard",
  employee: (id) => `/employee/${id}`,
  sifa: (id) => `/admin/${id}`,
  ama: (id) => `/admin/${id}`,
  external_sifa: (id) => `/admin/${id}`,
  admin: (id) => `/admin/${id}`,
};

interface TokenReader {
  readonly accessToken: string | undefined;
  readonly refreshToken: string | undefined;
}

const defaultTokenReader = (): TokenReader => ({
  accessToken: Cookies.get("access_token"),
  refreshToken: Cookies.get("refresh_token"),
});

function roleRedirectPath(userRole: string, userId: string | null): string {
  const toPath = ROLE_REDIRECTS[userRole];
  return toPath ? toPath(userId ?? "") : "/auth/login";
}

function isInspectionsPath(pathname: string): boolean {
  return pathname === "/inspections" || pathname.startsWith("/inspections/");
}

/**
 * Redirects visitors according to the requested path, available tokens, and
 * authenticated role. Returns normally when navigation is allowed.
 *
 * `readTokens` is injectable for tests; defaults to reading the real cookies.
 * `lastKnownAccessToken` is the access token the device last held, expired
 * or not; only the inspection app reads it (see below).
 */
export function applyAuthGuard(
  pathname: string,
  search: URLSearchParams = new URLSearchParams(),
  readTokens: () => TokenReader = defaultTokenReader,
  lastKnownAccessToken?: string,
): void {
  if ((OPEN_ROUTES as readonly string[]).includes(pathname)) return;
  const { accessToken, refreshToken } = readTokens();
  const isPublicRoute = (PUBLIC_ROUTES as readonly string[]).includes(pathname);
  const query = search.toString();
  const callbackPath = query ? `${pathname}?${query}` : pathname;

  if (!accessToken && !refreshToken) {
    if (!isPublicRoute) {
      throw redirect({ to: "/auth/login", search: { callback: callbackPath } });
    }
    return;
  }

  if (!accessToken && refreshToken) {
    // The inspection app runs from data on the device, so an inspector whose
    // access token has expired, often offline on site, keeps working; the
    // first request that reaches the server refreshes the session. The role
    // is read from the claims of the last access token the device held.
    if (lastKnownAccessToken && isInspectionsPath(pathname)) {
      const lastRole = getUserRoleFromToken(lastKnownAccessToken);
      if (lastRole && PROTECTED_ROUTES["/inspections"]!.includes(lastRole)) {
        return;
      }
      if (lastRole) {
        throw redirect({
          to:
            roleRedirectPath(
              lastRole,
              getUserIdFromToken(lastKnownAccessToken),
            ) || "/",
        });
      }
    }
    if (!pathname.includes("/auth/refresh") && !isPublicRoute) {
      throw redirect({
        to: "/auth/refresh",
        search: { callback: callbackPath },
      });
    }
    return;
  }

  // The remaining branches handle authenticated navigation.
  const userRole = getUserRoleFromToken(accessToken) ?? "employee";
  const userId = getUserIdFromToken(accessToken);

  if (pathname === "/") {
    throw redirect({ to: roleRedirectPath(userRole, userId) });
  }

  for (const [route, allowedRoles] of Object.entries(PROTECTED_ROUTES)) {
    const underRoute = pathname === route || pathname.startsWith(`${route}/`);
    if (underRoute && !allowedRoles.includes(userRole)) {
      throw redirect({ to: roleRedirectPath(userRole, userId) || "/" });
    }
  }

  if (AUTH_REDIRECT_ROUTES.includes(pathname)) {
    const callback = search.get("callback");
    // Resume wherever the visitor was headed, not just /inspections: any
    // in-app path is a legitimate destination once they are authenticated.
    if (isSameOriginPath(callback) && !isAuthRedirectRoute(callback)) {
      throw redirect({ to: callback });
    }
    throw redirect({ to: roleRedirectPath(userRole, userId) || "/" });
  }
}
